import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useFormik } from 'formik';
import { Link, useHistory} from 'react-router-dom';
import '../../dist/assets/stylesheets/css/login.css';
import {
  LogIn,
  logInWithGoogleCredential,
  isLoginSuccessResponse,
  getLoginToken,
} from '../util/loginAPI';
import { formatAuthFailureMessage } from '../util/authErrors';
import logo from './logo.PNG';
import { Icon } from '@iconify/react';

function getGoogleClientId() {
  if (typeof window !== 'undefined' && window.__GOOGLE_CLIENT_ID__) {
    const w = String(window.__GOOGLE_CLIENT_ID__).trim();
    if (w) return w;
  }
  // Webpack DefinePlugin replaces `process.env.GOOGLE_CLIENT_ID` with a string literal in the bundle.
  // Do not guard on `typeof process`: in the browser `process` is usually undefined, so the ID was never used.
  const id = process.env.GOOGLE_CLIENT_ID;
  return id ? String(id).trim() : '';
}

function emailFromGoogleJwt(credential) {
  try {
    const part = credential.split('.')[1];
    let b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4;
    if (pad) b64 += '='.repeat(4 - pad);
    const payload = JSON.parse(atob(b64));
    return payload.email || '';
  } catch (e) {
    return '';
  }
}

function LoginPage() {
  const formik = useFormik({
      initialValues: {
          email: "Enter your email",
          password: "Enter your Password",
      }
  })

  const [incorrectPassword, setIncorrectPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const {values, setFieldValue } = formik;
  const history = useHistory()
  const [modifiedE, setModifiedE] = useState(false);
  const [modifiedP, setModifiedP] = useState(false);
  const inputStyle = { width: "100%", height: "40px", fontSize: "16px", marginBottom: "20px" }
  const placeholderStyle = {color: 'grey', opacity: '0.8'}

  const googleCancelRef = useRef(false);
  const googleClientId = useMemo(() => getGoogleClientId(), []);

  const completeLoginSuccess = useCallback((email, token) => {
    localStorage.setItem('isLoggedIn', true);
    localStorage.setItem('email', email);
    localStorage.setItem('token', token);
    setIncorrectPassword(false);
    setErrorMessage('');
    const nextPath = '/devices';
    const applyNavigation = () => {
      try {
        history.replace(nextPath);
      } catch (e) {
        console.warn('history.replace after login', e);
      }
      const expectedHash = `#${nextPath}`;
      if (typeof window !== 'undefined' && window.location.hash !== expectedHash) {
        window.location.hash = expectedHash;
      }
    };
    if (typeof queueMicrotask === 'function') {
      queueMicrotask(applyNavigation);
    } else {
      window.setTimeout(applyNavigation, 0);
    }
  }, [history]);

  const handleGoogleCredential = useCallback(async (credential) => {
    if (!credential) return;
    setIncorrectPassword(false);
    setErrorMessage('');
    try {
      const res = await logInWithGoogleCredential(credential);
      const token = getLoginToken(res);
      if (isLoginSuccessResponse(res) && token) {
        const email = (res.email && String(res.email).trim()) || emailFromGoogleJwt(credential);
        completeLoginSuccess(email, token);
      } else {
        localStorage.setItem('isLoggedIn', false);
        setIncorrectPassword(true);
        setErrorMessage(
          (res && res.reason) ||
            (isLoginSuccessResponse(res) && !token
              ? 'Sign-in succeeded but the server did not return a token. Check the API response.'
              : 'Google sign-in failed. Please try again.')
        );
      }
    } catch (err) {
      console.log(err);
      setIncorrectPassword(true);
      setErrorMessage('Connection error. Please check your network connection and try again.');
    }
  }, [completeLoginSuccess]);

  const handleGoogleNotConfigured = useCallback(() => {
    setIncorrectPassword(true);
    setErrorMessage(
      'Google sign-in is not configured for this deployment. Set GOOGLE_CLIENT_ID when building, or window.__GOOGLE_CLIENT_ID__ in the page before the app loads (see README).'
    );
  }, []);

  useEffect(() => {
    if (!googleClientId) return undefined;
    googleCancelRef.current = false;

    const scriptSrc = 'https://accounts.google.com/gsi/client';
    const initGoogle = () => {
      if (googleCancelRef.current || typeof window === 'undefined' || !window.google?.accounts?.id) return;
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        ux_mode: 'popup',
        callback: (resp) => {
          if (resp && resp.credential) handleGoogleCredential(resp.credential);
        },
      });
      const el = document.getElementById('google-signin-button');
      if (el) {
        el.innerHTML = '';
        window.google.accounts.id.renderButton(el, {
          theme: 'outline',
          size: 'large',
          width: 400,
          text: 'continue_with',
          shape: 'rectangular',
        });
      }
    };

    let script = document.querySelector(`script[src="${scriptSrc}"]`);
    if (script) {
      if (window.google?.accounts?.id) initGoogle();
      else script.addEventListener('load', initGoogle);
      return () => {
        googleCancelRef.current = true;
        script.removeEventListener('load', initGoogle);
      };
    }

    script = document.createElement('script');
    script.src = scriptSrc;
    script.async = true;
    script.defer = true;
    script.onload = initGoogle;
    document.body.appendChild(script);

    return () => {
      googleCancelRef.current = true;
      script.onload = null;
    };
  }, [handleGoogleCredential, googleClientId]);

  let handleSubmit = async (e) => {
      e.preventDefault();
      setIncorrectPassword(false);
      setErrorMessage('');

      // Get actual values, ignoring placeholder text
      const email = values.email.trim();
      const password = values.password.trim();

      // Validate that fields are not empty or placeholder text
      if (!email || email === '' || email === 'Enter your email' || email === 'Enter your Email') {
        setIncorrectPassword(true);
        setErrorMessage('Please enter your email address');
        return;
      }

      if (!password || password === '' || password === 'Enter your Password') {
        setIncorrectPassword(true);
        setErrorMessage('Please enter your password');
        return;
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        setIncorrectPassword(true);
        setErrorMessage('Please enter a valid email address');
        return;
      }

      try {
        let res = await LogIn({email, password});
        console.log(res);
        window.res = res;
        const pwToken = getLoginToken(res);
        if (isLoginSuccessResponse(res) && pwToken) {
          const resolvedEmail = (res.email && String(res.email).trim()) || email;
          completeLoginSuccess(resolvedEmail, pwToken);
        }
        else {
          localStorage.setItem('isLoggedIn', false);
          setIncorrectPassword(true);
          setErrorMessage(
            isLoginSuccessResponse(res) && !pwToken
              ? 'Sign-in succeeded but the server did not return a token. Contact support.'
              : formatAuthFailureMessage(res, res && res.httpStatus)
          );
        }
      } catch (err) {
        console.log(err);
        setIncorrectPassword(true);
        setErrorMessage('Connection error. Please check your network connection and try again.');
      }
    };

  return (<div id="login-page">
    <section id="login-container">
      <div style={{ textAlign: "center" }}>
      <img src={logo} alt="Logo" style={{ width: '400px', height: '130px' , marginBottom: "40px"}} />
      </div>

      <form onSubmit={handleSubmit} id="login-form">
      {incorrectPassword && errorMessage ? (
        <div className="error-message" role="alert">
          {errorMessage}
        </div>
      ) : null}

      <>
        {googleClientId ? (
          <div id="google-signin-button" className="login-google-mount" aria-label="Sign in with Google" />
        ) : (
          <button
            type="button"
            className="login-google-fallback"
            onClick={handleGoogleNotConfigured}
            aria-label="Continue with Google"
          >
            <Icon icon="logos:google-icon" style={{ width: 22, height: 22 }} />
            Continue with Google
          </button>
        )}
        <div className="login-divider"><span>or</span></div>
      </>

      <label style={{ fontSize:"16px", display: "block", fontWeight: "bold", marginBottom: "8px"}}> Email </label>
      <input
          onChange={e => {setFieldValue("email", e.target.value);
                          setModifiedE(true);
                          }}
          className="login-input"
          value={values.email}
          onFocus={() => {if (!modifiedE)setFieldValue("email", "")}}
          style={Object.assign({}, inputStyle, modifiedE ? null : placeholderStyle)}
          onBlur={() => {
            if (values.email === "" || values.email.trim() === "") {
              setFieldValue("email","Enter your email");
              setModifiedE(false);
            }
          }}
        />

        <label style={{ fontSize:"16px", display: "block", fontWeight: "bold", marginBottom: "8px"}}> Password </label>

        <div style={{ display: "flex" }}>
          <input
            onChange={e => {setFieldValue("password", e.target.value);
            setModifiedP(true); }}
            className="login-input"
            type={showPassword ? "text" : "password"}
            style={Object.assign({}, inputStyle, modifiedP ? null : placeholderStyle)}
            value={values.password}
            onFocus={() => {if (!modifiedP)setFieldValue("password", "")}}
            onBlur={() => {
              if (values.password === "" || values.password.trim() === "") {
                setFieldValue("password","Enter your Password");
                setModifiedP(false);
              }
            }}
          />
          <div style={{ width: "10px" }}></div>

          <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ height: "40px", fontSize: "16px", marginBottom: "30px", width:"50px",fontWeight:"400" }}>
            {showPassword ? <Icon icon="clarity:eye-hide-line" /> : <Icon icon="mdi:show-outline" />}
          </button>
        </div>

        <div className='button-wrapper'>
          <button className="blue-button" type="submit" style={{marginBottom: "20px" }}>
            Log In
          </button>
          <span style={{textAlign:'center'}}>Don't have an account?
          <Link to="/signup" style={{marginLeft:"5px",color:'#63C7CF'}}>
            Sign up
          </Link>
          </span>
        </div>
      </form>
    </section>
    <section id="image-container">
      {/* <img src="https://images.squarespace-cdn.com/content/v1/5f6bccd37f6369040294ef40/3ccbb190-5dd3-4f03-a911-306cf7a5ec35/shutterstock_1476961856+copy+%281%29.jpg?format=1500w"></img> */}
    </section>
    </div>
  );
}

export default LoginPage;
